import { describeCode, verifyCodeCheck, type SuggestedCode } from '@gsls/shared';
import { useQuery, type QueryKey } from '@tanstack/react-query';
import { useEffect, useId, useState } from 'react';
import { inputClass, Tooltip } from './ui';

/**
 * A code field that asks the server for the next free code and shows what the
 * code it holds means.
 *
 * Auto-fill stops the moment someone types in the field: the suggestion is
 * guidance, and a person may need their own code (a lot often has to carry the
 * supplier's batch number). The regenerate button asks the server again, which
 * is also how you recover after a "code already taken" conflict.
 *
 * Deliberately not built on `Field`: the button must not sit inside the
 * wrapping `<label>`, or it would become the first labelable descendant and
 * steal the label's association from the input. The markup below mirrors
 * `Field` with the button as the input's sibling.
 */
export function CodeField({
  label,
  value,
  onChange,
  queryKey,
  queryFn,
  hint,
  tooltip,
  disabled = false,
  required = true,
  error,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  /** Enables server suggestion. Omit when editing: codes are immutable. */
  queryKey?: QueryKey;
  queryFn?: () => Promise<SuggestedCode>;
  hint?: string;
  tooltip?: string;
  disabled?: boolean;
  required?: boolean;
  /** A submit error about this field, e.g. the code already exists. */
  error?: string;
  placeholder?: string;
}) {
  const id = useId();
  const [touched, setTouched] = useState(false);

  const suggestion = useQuery<SuggestedCode>({
    queryKey: queryKey ?? ['code-suggestion', id],
    queryFn: queryFn ?? (() => Promise.resolve({ code: '', segments: [] })),
    enabled: Boolean(queryFn),
  });

  // Fill the field from the server until the person types over it.
  useEffect(() => {
    if (!queryFn || touched) return;
    const code = suggestion.data?.code;
    if (code && code !== value) onChange(code);
  }, [queryFn, touched, suggestion.data, value, onChange]);

  const segments = value.trim() ? describeCode(value) : [];
  const checkValid = value.trim() ? verifyCodeCheck(value.trim()) : null;

  const status = queryFn
    ? suggestion.isError
      ? 'Could not fetch a suggestion — type a code or try again.'
      : suggestion.isFetching
        ? 'Finding the next free number…'
        : 'Suggested from this record — type over it to use your own code.'
    : null;

  return (
    <div className="block">
      <div className="mb-1 flex items-center gap-1.5">
        <label htmlFor={id} className="block text-sm font-bold text-ink">
          {label}
        </label>
        {tooltip ? <Tooltip text={tooltip} subject={label} /> : null}
      </div>

      <div className="flex gap-2">
        <input
          id={id}
          name={label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}
          className={`${inputClass} font-mono`}
          value={value}
          onChange={(event) => {
            setTouched(true);
            onChange(event.target.value);
          }}
          disabled={disabled}
          required={required}
          autoComplete="off"
          placeholder={placeholder ?? (queryFn ? 'auto' : 'e.g. RM-003')}
        />
        {queryFn ? (
          <button
            type="button"
            onClick={() => {
              setTouched(false);
              void suggestion.refetch();
            }}
            disabled={disabled || suggestion.isFetching}
            title="Ask the server for the next free code"
            className="inline-flex h-11 shrink-0 items-center justify-center rounded-sm border border-surface-border bg-white px-3 text-base text-ink-muted transition hover:border-brand hover:text-brand-deep disabled:cursor-not-allowed disabled:opacity-50 sm:h-9 sm:text-sm"
          >
            ↻
          </button>
        ) : null}
      </div>

      {segments.length > 0 ? (
        <span className="mt-1.5 flex flex-wrap items-center gap-1 text-xs">
          {segments.map((segment, index) => (
            <span
              // Segments can repeat textually (`RCV` twice in one code), so the
              // position is the only stable key.
              key={`${index}-${segment.value}`}
              className="inline-flex items-center gap-1 rounded-full border border-surface-muted bg-surface px-2 py-0.5"
            >
              <span className="font-mono text-[11px] text-ink">{segment.value}</span>
              <span className="text-[11px] text-ink-muted">{segment.label}</span>
            </span>
          ))}
        </span>
      ) : null}

      {checkValid === false ? (
        <span className="mt-1 block text-xs text-amber-700">
          The check character doesn’t match — is the code mistyped?
        </span>
      ) : null}

      {hint || status ? (
        <span className="mt-1 block text-xs text-ink-muted">
          {hint}
          {hint && status ? ' ' : ''}
          {status}
        </span>
      ) : null}

      {error ? <span className="mt-1 block text-xs text-red-700">{error}</span> : null}
    </div>
  );
}
